import { useState, useCallback, useEffect } from 'react';
import { StepperStep } from '../../../types';
import type { EditingSection } from '../../../types';
import type { FormData } from '../../../types';
import { DesignViewEditSectionEnum } from '../../designs';

export const useCheckoutStepper = (
  checkoutSteps: StepperStep[],
  formData: FormData,
  initialEditingSection?: string | null
) => {
  const [steps, setSteps] = useState<StepperStep[]>(checkoutSteps);
  // Inicializa editingSection com o valor fornecido ou null
  const [editingSection, setEditingSection] = useState<DesignViewEditSectionEnum | null>(
    initialEditingSection as DesignViewEditSectionEnum || null
  );

  const updateStepperStatus = useCallback(() => {
    setSteps(prevSteps => {
      let changed = false;
      const newSteps = prevSteps.map(step => {
        const newStepInstance = new StepperStep(step.id, step.name);
        // @ts-ignore
        newStepInstance._completed = step._completed;
        if (newStepInstance.updateStatus(formData)) {
          changed = true;
          return newStepInstance;
        }
        return step;
      });
      return changed ? newSteps : prevSteps;
    });
  }, [formData]);

  // Determina qual etapa deve ser mostrada como "atual" no stepper
  const getCurrentStepId = useCallback(() => {
    // Se está editando uma seção específica, mostra essa seção como atual
    if (editingSection) {
      return editingSection;
    }
    
    // Se não está editando, determina qual etapa deve ser a atual baseada no progresso
    const completedSteps = steps.filter(step => step.status === 'completed');
    
    // Se todas as etapas estão concluídas, mostra a última como atual
    if (completedSteps.length === steps.length) {
      return steps[steps.length - 1].id;
    }
    
    // Se nenhuma etapa está concluída, mostra a primeira como atual
    if (completedSteps.length === 0) {
      return steps[0].id;
    }
    
    // Mostra a próxima etapa não concluída como atual
    const nextIncompleteStep = steps.find(step => step.status === 'pending');
    return nextIncompleteStep ? nextIncompleteStep.id : steps[0].id;
  }, [editingSection, steps]);

  const handleEditSection = useCallback((sectionId: EditingSection) => {
    setEditingSection(sectionId as DesignViewEditSectionEnum);
  }, [setEditingSection]);

  const handleStepClick = useCallback((step: StepperStep) => {
    // Se todas as etapas estão concluídas, permite ir para a revisão
    const allStepsCompleted = steps.every(s => s.status === 'completed');
    if (allStepsCompleted) {
      setEditingSection(null); // Vai para a tela de revisão
      return;
    }
    
    // Só permite navegar para etapas que estão concluídas ou são a próxima disponível
    const stepIndex = steps.findIndex(s => s.id === step.id);
    const canNavigateToStep = step.status === 'completed' || 
      (stepIndex > 0 && steps[stepIndex - 1].status === 'completed') ||
      stepIndex === 0; // Sempre pode ir para a primeira etapa
    
    if (canNavigateToStep) {
      setEditingSection(step.id as DesignViewEditSectionEnum);
    }
  }, [steps, setEditingSection]);

  const navigateToNextSection = useCallback(() => {
    setEditingSection(prevEditingSection => {
      const idxCurrentSection = checkoutSteps.findIndex(step => step.id === prevEditingSection);
      const nextSection = checkoutSteps[idxCurrentSection + 1];
      return nextSection ? (nextSection.id === 'revisao' ? null : nextSection.id as DesignViewEditSectionEnum) : null;
    });
  }, [checkoutSteps, setEditingSection]);

  useEffect(() => {
    updateStepperStatus();
  }, [editingSection, formData, updateStepperStatus]);

  return {
    steps,
    editingSection,
    setEditingSection,
    getCurrentStepId,
    handleEditSection,
    handleStepClick,
    navigateToNextSection,
  };
}; 